import { SettingsPage } from './features/settings/SettingsPage.js';
import { NotificationPreferencesPage } from './features/notifications/NotificationPreferencesPage.js';
import { PushSync } from './features/push/PushSync.js';
import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate } from '@azure/msal-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { msalInstance } from './lib/msalConfig.js';
import { queryClient } from './lib/queryClient.js';
import { RequirePermission, RequireReports, RequireSuperAdmin } from './components/RequireRole.js';
import { KnowledgeArea } from './features/knowledge/KnowledgeArea.js';
import { LoginPage } from './features/auth/LoginPage.js';
import { ChatPage } from './features/chat/ChatPage.js';
import { UsagePage } from './features/usage/UsagePage.js';
import { UsageConversationPage } from './features/usage/UsageConversationPage.js';
import { CapabilitiesPage } from './features/integrations/CapabilitiesPage.js';
import { CapabilityDetailPage } from './features/integrations/CapabilityDetailPage.js';
import { ConnectionsPage } from './features/integrations/ConnectionsPage.js';
import { IntegrationFormPage } from './features/integrations/IntegrationFormPage.js';
import { McpConnectionPage } from './features/integrations/McpConnectionPage.js';
import { UsersPage } from './features/users/UsersPage.js';
import { UserManagePage } from './features/users/UserManagePage.js';
import { ApiKeysPage } from './features/apiKeys/ApiKeysPage.js';
import { GroupsPage } from './features/groups/GroupsPage.js';
import { GroupManagePage } from './features/groups/GroupManagePage.js';
import { ScopesPage } from './features/scopes/ScopesPage.js';
import { ServicePackagesPage } from './features/service-packages/ServicePackagesPage.js';
import { EventDefinitionPage } from './features/events/EventDefinitionPage.js';
import { EventsPage } from './features/events/EventsPage.js';
import { FeedbackPage } from './features/feedback/FeedbackPage.js';
import { ConversationPage } from './features/feedback/ConversationPage.js';
import { BuilderListPage } from './features/workflowBuilder/BuilderListPage.js';
import { TemplateGalleryPage } from './features/workflowBuilder/TemplateGalleryPage.js';
import { ActivityPage } from './features/workflowBuilder/ActivityPage.js';
import { BuilderEditorPage } from './features/workflowBuilder/BuilderEditorPage.js';
import { BuilderRunsPage } from './features/workflowBuilder/BuilderRunsPage.js';
import { WebhooksPage } from './features/webhooks/WebhooksPage.js';
import { GuardrailsRoutes } from './features/guardrails/GuardrailsRoutes.js';
import { BotsPage } from './features/bots/BotsPage.js';
import { BotConfigPage } from './features/bots/BotConfigPage.js';
import { AssistantPage } from './features/assistant/AssistantPage.js';
import { ReportsPage } from './features/reports/ReportsPage.js';
import { ReportTemplatePage } from './features/reports/ReportTemplatePage.js';
import { useMe } from './hooks/useMe.js';
import { PERMS, adminLandingPath } from './lib/permissions.js';

/** /admin → the first nav surface the caller holds a permission for. */
const AdminLanding = () => {
  const me = useMe();
  if (me.isLoading) return null;
  return <Navigate to={me.data ? adminLandingPath(me.data.user) : '/'} replace />;
};

/**
 * Single platform client. Chat is the primary surface (`/`), gated on the
 * `Agent.Run` scope inside ChatPage. The admin area is one shell reached from
 * the user menu's "Admin" link, shown to anyone holding ANY admin permission;
 * each page (and its nav item) declares the permission it needs — roles gate
 * nav items the way scopes gate agent tools. Failing a gate redirects to chat.
 */
export const App = () => (
  <MsalProvider instance={msalInstance}>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <UnauthenticatedTemplate>
          <LoginPage />
        </UnauthenticatedTemplate>
        <AuthenticatedTemplate>
          <PushSync />
          <Routes>
            <Route path="/settings" element={<SettingsPage />} />
            <Route
              path="/settings/connected-accounts"
              element={<SettingsPage tab="connected-accounts" />}
            />
            <Route
              path="/settings/my-notifications"
              element={<Navigate to="/settings" replace />}
            />
            <Route
              path="/settings/notification-defaults"
              element={
                <RequirePermission anyOf={[PERMS.notifications.manage]}>
                  <NotificationPreferencesPage organization />
                </RequirePermission>
              }
            />
            <Route path="/" element={<ChatPage />} />
            {/* Every conversation has an address; the bare '/' redirects here. */}
            <Route path="/chat/:conversationId" element={<ChatPage />} />
            <Route path="/shared/:shareId" element={<ChatPage />} />

            {/* The Admin link lands on the first surface the caller may see —
                an admin gets Usage as before; a KNOWLEDGE_ADMIN gets Categories. */}
            <Route path="/admin" element={<AdminLanding />} />
            {/* The Reports scopes open the pages, the same ones the API checks. */}
            <Route
              path="/reports"
              element={
                <RequireReports>
                  <ReportsPage />
                </RequireReports>
              }
            />
            <Route
              path="/reports/templates/:id"
              element={
                <RequireReports>
                  <ReportTemplatePage />
                </RequireReports>
              }
            />
            <Route
              path="/knowledge/*"
              element={
                <RequirePermission anyOf={[PERMS.knowledge.view]}>
                  <KnowledgeArea />
                </RequirePermission>
              }
            />
            <Route
              path="/usage"
              element={
                <RequirePermission anyOf={[PERMS.usage.view]}>
                  <UsagePage />
                </RequirePermission>
              }
            />
            <Route
              path="/usage/conversations/:conversationId"
              element={
                <RequirePermission anyOf={[PERMS.usage.view]}>
                  <UsageConversationPage />
                </RequirePermission>
              }
            />
            {/* Feedback = the review queue: rated turns the pipeline left
                open, routed by a human (dismiss / send to KB / escalate). */}
            <Route
              path="/feedback"
              element={
                <RequirePermission anyOf={[PERMS.feedback.view, PERMS.knowledge.view]}>
                  <FeedbackPage />
                </RequirePermission>
              }
            />
            {/* The full thread gets its own page — inline expansion overwhelmed
                the queue (user call). */}
            <Route
              path="/feedback/conversation/:conversationId"
              element={
                <RequirePermission anyOf={[PERMS.feedback.view, PERMS.knowledge.view]}>
                  <ConversationPage />
                </RequirePermission>
              }
            />
            {/* Events = a flat observability list over the pipeline (row click
                expands the raw record — no detail page by design). */}
            <Route
              path="/events/register"
              element={
                <RequirePermission anyOf={[PERMS.events.view]}>
                  <EventDefinitionPage />
                </RequirePermission>
              }
            />
            <Route
              path="/events/catalog/:eventTypeId/edit"
              element={
                <RequirePermission anyOf={[PERMS.events.view]}>
                  <EventDefinitionPage />
                </RequirePermission>
              }
            />
            <Route
              path="/events"
              element={
                <RequirePermission anyOf={[PERMS.events.view]}>
                  <EventsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/settings/webhooks"
              element={
                <RequirePermission anyOf={[PERMS.webhooks.manage]}>
                  <WebhooksPage />
                </RequirePermission>
              }
            />
            <Route
              path="/settings/workflows"
              element={
                <RequirePermission anyOf={[PERMS.workflows.manage]}>
                  <BuilderListPage />
                </RequirePermission>
              }
            />
            <Route
              path="/settings/workflows/activity"
              element={
                <RequirePermission anyOf={[PERMS.workflows.manage]}>
                  <ActivityPage />
                </RequirePermission>
              }
            />
            <Route
              path="/settings/workflows/templates"
              element={
                <RequirePermission anyOf={[PERMS.workflows.manage]}>
                  <TemplateGalleryPage />
                </RequirePermission>
              }
            />
            <Route
              path="/settings/workflows/:workflowId"
              element={
                <RequirePermission anyOf={[PERMS.workflows.manage]}>
                  <BuilderEditorPage />
                </RequirePermission>
              }
            />
            <Route
              path="/settings/workflows/:workflowId/runs"
              element={
                <RequirePermission anyOf={[PERMS.workflows.manage]}>
                  <BuilderRunsPage />
                </RequirePermission>
              }
            />
            {/* Capabilities = what the agent can do + which source feeds each.
                Old /integrations links (and the retired vendor pages) land here. */}
            <Route path="/integrations/*" element={<Navigate to="/capabilities" replace />} />
            <Route
              path="/capabilities"
              element={
                <RequirePermission anyOf={[PERMS.integrations.manage]}>
                  <CapabilitiesPage />
                </RequirePermission>
              }
            />
            <Route
              path="/capabilities/:capability"
              element={
                <RequirePermission anyOf={[PERMS.integrations.manage]}>
                  <CapabilityDetailPage />
                </RequirePermission>
              }
            />
            {/* Connections = the accounts a tenant authenticates once, reused
                across capabilities. Set-up/edit forms live under it. */}
            <Route
              path="/connections"
              element={
                <RequirePermission anyOf={[PERMS.integrations.manage]}>
                  <ConnectionsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/connections/:integrationKey/connect"
              element={
                <RequirePermission anyOf={[PERMS.integrations.manage]}>
                  <IntegrationFormPage mode="create" />
                </RequirePermission>
              }
            />
            <Route
              path="/connections/:integrationKey/edit"
              element={
                <RequirePermission anyOf={[PERMS.integrations.manage]}>
                  <IntegrationFormPage mode="edit" />
                </RequirePermission>
              }
            />
            {/* A Custom MCP connection's approved tools and allowed groups. */}
            <Route
              path="/connections/:integrationId/tools"
              element={
                <RequirePermission anyOf={[PERMS.integrations.manage]}>
                  <McpConnectionPage />
                </RequirePermission>
              }
            />
            <Route
              path="/users"
              element={
                <RequirePermission anyOf={[PERMS.users.manage]}>
                  <UsersPage />
                </RequirePermission>
              }
            />
            <Route
              path="/users/:userId"
              element={
                <RequirePermission anyOf={[PERMS.users.manage]}>
                  <UserManagePage />
                </RequirePermission>
              }
            />
            <Route
              path="/settings/groups"
              element={
                <RequirePermission anyOf={[PERMS.users.manage]}>
                  <GroupsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/settings/groups/:groupId"
              element={
                <RequirePermission anyOf={[PERMS.users.manage]}>
                  <GroupManagePage />
                </RequirePermission>
              }
            />
            <Route
              path="/settings/bots"
              element={
                <RequirePermission anyOf={[PERMS.bots.manage]}>
                  <BotsPage />
                </RequirePermission>
              }
            />
            <Route
              path="/settings/bots/:botId"
              element={
                <RequirePermission anyOf={[PERMS.bots.manage]}>
                  <BotConfigPage />
                </RequirePermission>
              }
            />
            <Route
              path="/settings/guardrails/*"
              element={
                <RequirePermission anyOf={[PERMS.assistant.manage]}>
                  <GuardrailsRoutes />
                </RequirePermission>
              }
            />
            <Route
              path="/settings/assistant"
              element={
                <RequirePermission anyOf={[PERMS.assistant.manage]}>
                  <AssistantPage />
                </RequirePermission>
              }
            />
            <Route
              path="/settings/api-keys"
              element={
                <RequirePermission anyOf={[PERMS.apiKeys.manage]}>
                  <ApiKeysPage />
                </RequirePermission>
              }
            />
            <Route
              path="/settings/scopes"
              element={
                <RequireSuperAdmin>
                  <ScopesPage />
                </RequireSuperAdmin>
              }
            />
            <Route
              path="/settings/service-packages"
              element={
                <RequireSuperAdmin>
                  <ServicePackagesPage />
                </RequireSuperAdmin>
              }
            />
            <Route
              path="/settings/modules"
              element={
                <RequireSuperAdmin>
                  <Navigate to="/settings/service-packages" replace />
                </RequireSuperAdmin>
              }
            />
          </Routes>
        </AuthenticatedTemplate>
      </BrowserRouter>
    </QueryClientProvider>
  </MsalProvider>
);
